import { ChoiceGroup, CommandBarButton, DatePicker, Label, MaskedTextField, Panel, PanelType, PrimaryButton, Spinner, Stack, TextField, Toggle } from '@fluentui/react';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { createAccount } from '../../api/accounts';
import { GENDER_TYPE, PATIENT_LANGUAGES } from '../../config/constants';
import { formatDate } from '../../util/Date';

interface Props {
  onChange: (account: any) => void;
}

export function CreatePatient({ onChange }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className="flex" style={{ height: 32 }}>
        <CommandBarButton iconProps={{ iconName: 'Add' }} text="Create Patient" data-tid="btn_createPatient" onClick={() => setOpen(true)} />
      </div>
      {open && (
        <Panel isBlocking={false} isOpen={true} onDismiss={() => setOpen(false)} type={PanelType.medium} headerText="Create Patient">
          <PatientForm
            onChange={(account) => {
              setOpen(false);
              onChange(account);
            }}
          />
        </Panel>
      )}
    </>
  );
}

function PatientForm({ onChange }: { onChange: (account: any) => void }) {
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', gender: '', preferredLanguage: '', dateOfBirth: undefined, zipcode: '', phoneNumber: '' });
  const [noEmail, setNoEmail] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (noEmail) {
      const r = Math.random().toString(3).substring(2, 5);
      setFormData({ ...formData, email: `noemail+${formData.firstName.replace(/ /g, '')}.${formData.lastName.replace(/ /g, '')}.${r}@heal.com` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noEmail]);

  const canCreate = useMemo(() => {
    return (
      !saving && !!formData.firstName && !!formData.lastName && !!formData.phoneNumber && !!formData.zipcode && !!formData.dateOfBirth && !!formData.gender && !!formData.email
    );
  }, [formData, saving]);

  const create = () => {
    setSaving(true);
    createAccount(formData)
      .then(onChange)
      .finally(() => setSaving(false));
  };

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <TextField
        name="firstName"
        value={formData.firstName}
        onChange={(e, v) => setFormData({ ...formData, firstName: v! })}
        label="First Name"
        required
        disabled={saving}
        data-tid="inp_firstName"
      />

      <TextField
        name="lastName"
        value={formData.lastName}
        onChange={(e, v) => setFormData({ ...formData, lastName: v! })}
        label="Last Name"
        required
        disabled={saving}
        data-tid="inp_lastName"
      />

      <Stack horizontal tokens={{ childrenGap: 6 }}>
        <TextField
          name="email"
          style={{ width: 420 }}
          value={formData.email}
          onChange={(e, v) => setFormData({ ...formData, email: _.trim(v) })}
          label="E-mail"
          required
          disabled={saving || noEmail}
          data-tid="inp_email"
        />
        <Toggle checked={noEmail} onChange={(e, c) => setNoEmail(c!)} label="No email, auto-generate" data-tid="btn_noEmail" disabled={saving} />
      </Stack>

      <ChoiceGroup
        className="inline-flex"
        defaultSelectedKey={formData.gender}
        options={GENDER_TYPE.map((m) => ({ key: m.text.toUpperCase(), text: m.text }))}
        onChange={(e, o) => setFormData({ ...formData, gender: (o?.key as string) || '' })}
        label="Sex"
        required
        disabled={saving}
        data-tid="btn_gender"
      />

      <ChoiceGroup
        className="inline-flex"
        defaultSelectedKey={formData.preferredLanguage}
        options={Object.entries(PATIENT_LANGUAGES).map(([key, value]) => ({ key, text: value }))}
        onChange={(e, o) => setFormData({ ...formData, preferredLanguage: (o?.key as string) || '' })}
        label="Preferred Language"
        required
        disabled={saving}
        data-tid="btn_language"
      />

      <Stack>
        <Label required>Date of Birth (mm/dd/yyyy)</Label>
        <DatePicker
          onSelectDate={(d) => setFormData({ ...formData, dateOfBirth: d instanceof Date ? (d as any) : undefined })}
          value={formData.dateOfBirth}
          allowTextInput
          disableAutoFocus
          formatDate={formatDate}
          disabled={saving}
          data-tid="inp_DOB"
        />
      </Stack>

      <MaskedTextField
        name="zipcode"
        value={formData.zipcode}
        onChange={(e, v) => setFormData({ ...formData, zipcode: _.trim(v) })}
        label="Home ZIP Code"
        required
        mask="99999"
        maskChar=""
        maxLength={5}
        disabled={saving}
        data-tid="inp_zip"
      />

      <MaskedTextField
        name="phone"
        value={formData.phoneNumber}
        onChange={(e, v) => setFormData({ ...formData, phoneNumber: _.trim(v) })}
        label="Phone Number"
        required
        mask="999-999-9999"
        maskChar=""
        maxLength={14}
        disabled={saving}
        data-tid="inp_phone"
      />
      <br />
      <PrimaryButton onClick={create} text="Create" disabled={!canCreate} />
      {saving && <Spinner />}
    </Stack>
  );
}
